import { merge } from 'lodash'

/**
 * Локальное состояние выпадающего меню.
 *
 * @typedef {object} MenuState
 * @property {boolean} _showDropdown Признак раскрытия меню.
 */

/**
 * Выпадающее меню списка: параметры меню, признак раскрытия и
 * ссылки на само меню и контейнер привязки.
 */
export const menu = {
  props: {
    /**
     * Дополнительные параметры выпадающего меню. Дополняют и
     * переопределяют параметры, вычисленные свойством $_menuProps.
     *
     * @type {object|Array<*>|string}
     * @default {}
     */
    menuProps: {
      type: [ Object, Array, String ],
      default: () => {
        return {}
      }
    }
  },

  /**
   * Локальное состояние выпадающего меню.
   *
   * @returns {MenuState} Локальное состояние.
   */
  data() {
    return {
      _showDropdown: false
    }
  },

  computed: {
    /**
     * Отдает компонент выпадающего меню.
     * Если ссылка еще не установлена, отдается undefined.
     *
     * @returns {object|undefined} Компонент выпадающего меню.
     */
    menu() {
      return this.$refs && this.$refs.menu || undefined
    },
    /**
     * Отдает элемент, к которому привязывается выпадающее меню.
     * Если ссылка еще не установлена, отдается undefined.
     *
     * @returns {HTMLElement|undefined} Элемент привязки меню.
     */
    attach() {
      return this.$refs && this.$refs.attach && (this.$refs.attach.$el || this.$refs.attach)
    },

    /**
     * Отдает параметры выпадающего меню.
     * Ширина меню ограничивается шириной корневого элемента,
     * если он уже отрисован, иначе ограничение не задается.
     * Закрытие по нажатию на содержимое включено, только когда нет
     * multiple. Параметры из menuProps переопределяют вычисленные.
     *
     * @returns {object} Параметры выпадающего меню.
     */
    $_menuProps() {
      let elementWidth = null
      if (this.$el) {
        elementWidth = this.$el.offsetWidth
      }

      return merge(
        {
          value: this._data._showDropdown,
          attach: this.attach,
          closeOnClick: false,
          closeOnContentClick: !this.multiple,
          maxHeight: 300,
          minWidth: elementWidth,
          maxWidth: elementWidth,
          offsetDistance: 2,
          rounded: true,
          overflowY: 'auto'
        },
        this.menuProps
      )
    }
  },

  methods: {
    /**
     * Пересчитывает размеры и положение выпадающего меню после
     * обновления содержимого.
     * Если меню еще не создано или не поддерживает пересчет
     * размеров, ничего не происходит.
     */
    _updateMenu() {
      this.$nextTick(() => {
        if (this.menu && this.menu.updateDimensions) {
          this.menu.updateDimensions()
        }
      })
    }
  }
}
